import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
document.body.style.margin = '0';
document.body.style.background = '#F9FAFB';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000' });
API.interceptors.request.use(cfg => {
  cfg.withCredentials = true;
  return cfg;
});

// ── Shared styles ─────────────────────────────────────────────────────────────
const INPUT = {
  width: '100%', padding: '11px 14px', borderRadius: 8,
  border: '1.5px solid #E5E7EB', fontSize: 14, color: '#111827',
  outline: 'none', boxSizing: 'border-box', background: '#FAFAFA',
  transition: 'border-color 0.15s',
  fontFamily: "'DM Sans', sans-serif",
};

const LABEL = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: '#374151', marginBottom: 6, letterSpacing: '0.02em',
};

const PrimaryBtn = ({ onClick, disabled, children }) => (
  <button onClick={onClick} disabled={disabled} style={{
    width: '100%', padding: '12px', borderRadius: 8, border: 'none',
    background: disabled ? '#BFDBFE' : '#1D4ED8',
    color: 'white', fontSize: 14, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background 0.15s', letterSpacing: '0.01em',
    fontFamily: "'DM Sans', sans-serif",
  }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = '#1E40AF'; }}
    onMouseLeave={e => { if (!disabled) e.currentTarget.style.background = '#1D4ED8'; }}
  >
    {children}
  </button>
);

const SocialBtn = ({ icon, label }) => (
  <button style={{
    flex: 1, padding: '10px 12px', borderRadius: 8, border: '1.5px solid #E5E7EB',
    background: 'white', color: '#374151', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, transition: 'all 0.15s', fontFamily: "'DM Sans', sans-serif",
  }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = '#1D4ED8'; e.currentTarget.style.background = '#F0F4FF'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.background = 'white'; }}
  >
    {icon}
    <span>{label}</span>
  </button>
);

const ErrorBox = ({ msg }) => (
  <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginBottom: 18 }}>
    <p style={{ margin: 0, fontSize: 13, color: '#991B1B', fontWeight: 500 }}>{msg}</p>
  </div>
);

const Divider = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
    <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
    <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>or continue with email</span>
    <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
  </div>
);

// ── Right panel decorative elements ──────────────────────────────────────────
const RightPanel = () => (
  <div style={{
    flex: 1,
    background: 'linear-gradient(145deg, #1D4ED8 0%, #1E40AF 40%, #1E3A8A 100%)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    position: 'relative', overflow: 'hidden',
  }}>
    {/* Background circles */}
    <div style={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.08)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }} />
    <div style={{ position: 'absolute', width: 600, height: 600, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.05)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }} />
    <div style={{ position: 'absolute', width: 240, height: 240, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }} />
    <p style={{ position: 'absolute', bottom: 28, fontSize: 12, color: 'rgba(255,255,255,0.4)', textAlign: 'center', fontFamily: "'DM Sans', sans-serif" }}>
      Connect with every compliance workflow.
    </p>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const Login = () => {
  const navigate = useNavigate();
  const [step,     setStep]     = useState('password');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [code,     setCode]     = useState('');
  const [preToken, setPreToken] = useState('');
  const [qrCode,   setQrCode]   = useState('');
  const [secret,   setSecret]   = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const handlePassword = async () => {
    setLoading(true); setError('');
    try {
      const res = await API.post('/auth/login', { email, password });
      setPreToken(res.data.pre_token);
      if (res.data.requires_2fa_setup) {
        const setup = await API.post('/auth/2fa/setup', {}, { headers: { Authorization: `Bearer ${res.data.pre_token}` } });
        setQrCode(setup.data.qr_code); setSecret(setup.data.secret); setStep('setup');
      } else { setStep('verify'); }
    } catch (err) { setError(err.response?.data?.error || 'Invalid email or password'); }
    finally { setLoading(false); }
  };

  const handleVerifySetup = async () => {
    setLoading(true); setError('');
    try {
      const res = await API.post('/auth/2fa/verify-setup', { code }, { headers: { Authorization: `Bearer ${preToken}` } });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      navigate('/');
    } catch (err) { setError(err.response?.data?.error || 'Invalid code'); }
    finally { setLoading(false); }
  };

  const handleVerify = async () => {
    setLoading(true); setError('');
    try {
      const res = await API.post('/auth/2fa/verify', { code }, { headers: { Authorization: `Bearer ${preToken}` } });
      if (res.data.requires_device_trust) { navigate('/trust-device'); return; }
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      navigate('/');
    } catch (err) { setError(err.response?.data?.error || 'Invalid code'); }
    finally { setLoading(false); }
  };

  return (
    <>
      {/* Google Font */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');`}</style>

      <div style={{
        display: 'flex', minHeight: '100vh',
        fontFamily: "'DM Sans', sans-serif",
        background: '#F9FAFB',
      }}>
        {/* ── Left panel ── */}
        <div style={{
          width: isMobile ? '100%' : '45%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: isMobile ? '18px 24px 32px' : '48px 56px',
          background: 'white',
          minHeight: '100vh',
        }}>
          <div style={{ width: '100%', maxWidth: step === 'setup' ? 420 : 360 }}>

         {/* Logo */}
         <div style={{ marginBottom: 20, textAlign: 'center' }}>
              <img src="/images/logo.png" alt="RegShield" style={{ height: 36, objectFit: 'contain' }} />
            </div>

              {step === 'password' && (
                <>
                  <h1 style={{ margin: '0 0 6px', fontSize: 24, fontWeight: 700, color: '#111827', letterSpacing: '-0.02em', textAlign: isMobile ? 'center' : 'left' }}>
                    Log in to your Account
                  </h1>
                  <p style={{ margin: 0, fontSize: 14, color: '#6B7280' }}>
                    Luxembourg Compliance Platform
                  </p>
                </>
              )}
              {step === 'setup' && (
                <>
                  <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, color: '#111827', letterSpacing: '-0.02em' }}>
                    Set up two-factor authentication
                  </h1>
                  <p style={{ margin: 0, fontSize: 14, color: '#6B7280' }}>
                    Scan with Google Authenticator or Authy
                  </p>
                </>
              )}
              {step === 'verify' && (
                <>
                  <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, color: '#111827', letterSpacing: '-0.02em' }}>
                    Two-factor authentication
                  </h1>
                  <p style={{ margin: 0, fontSize: 14, color: '#6B7280' }}>
                    Enter the code from your authenticator app
                  </p>
                </>
              )}

            {/* ── Step 1: Password ── */}
            {step === 'password' && (
              <>
                {/* Social buttons */}
                <div style={{ display: 'flex', gap: 10, marginTop: 24, marginBottom: 4 }}>
                  <SocialBtn label="Google" icon={
                    <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  } />
                  <SocialBtn label="Facebook" icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  } />
                </div>

                <Divider />

                {error && <ErrorBox msg={error} />}

                <div style={{ marginBottom: 14 }}>
                  <label style={LABEL}>Email address</label>
                  <input type="email" placeholder="you@example.com" value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handlePassword()}
                    onFocus={e => e.target.style.borderColor = '#1D4ED8'}
                    onBlur={e => e.target.style.borderColor = '#E5E7EB'}
                    style={INPUT} />
                </div>

                <div style={{ marginBottom: 8 }}>
                  <label style={LABEL}>Password</label>
                  <input type="password" placeholder="••••••••" value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handlePassword()}
                    onFocus={e => e.target.style.borderColor = '#1D4ED8'}
                    onBlur={e => e.target.style.borderColor = '#E5E7EB'}
                    style={INPUT} />
                </div>

                <div style={{ textAlign: 'right', marginBottom: 22 }}>
                  <a href="/forgot-password" style={{ fontSize: 13, color: '#1D4ED8', textDecoration: 'none', fontWeight: 500 }}>
                    Forgot password?
                  </a>
                </div>

                <PrimaryBtn onClick={handlePassword} disabled={loading}>
                  {loading ? 'Signing in…' : 'Sign in'}
                </PrimaryBtn>
              </>
            )}

            {/* ── Step 2a: 2FA Setup ── */}
            {step === 'setup' && (
              <>
                {error && <ErrorBox msg={error} />}

                {qrCode && (
                  <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <img src={qrCode} alt="2FA QR Code" style={{ width: 180, height: 180, borderRadius: 12, border: '1.5px solid #E5E7EB' }} />
                  </div>
                )}

                <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '12px 14px', marginBottom: 20, border: '1.5px solid #E5E7EB' }}>
                  <p style={{ margin: '0 0 4px', fontSize: 10, color: '#6B7280', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Manual entry key</p>
                  <p style={{ margin: 0, fontSize: 12, fontFamily: 'monospace', color: '#111827', wordBreak: 'break-all' }}>{secret}</p>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={LABEL}>6-digit code from your app</label>
                  <input type="text" inputMode="numeric" maxLength={6} placeholder="000 000" value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={e => e.key === 'Enter' && handleVerifySetup()}
                    onFocus={e => e.target.style.borderColor = '#1D4ED8'}
                    onBlur={e => e.target.style.borderColor = '#E5E7EB'}
                    style={{ ...INPUT, letterSpacing: '0.4em', fontSize: 22, textAlign: 'center', fontWeight: 700 }}
                    autoFocus
                  />
                </div>

                <PrimaryBtn onClick={handleVerifySetup} disabled={loading || code.length !== 6}>
                  {loading ? 'Verifying…' : 'Activate 2FA & Sign in'}
                </PrimaryBtn>
              </>
            )}

            {/* ── Step 2b: 2FA Verify ── */}
            {step === 'verify' && (
              <>
                {error && <ErrorBox msg={error} />}

                <div style={{ marginBottom: 24 }}>
                  <label style={LABEL}>Authentication code</label>
                  <input type="text" inputMode="numeric" maxLength={6} placeholder="000 000" value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={e => e.key === 'Enter' && handleVerify()}
                    onFocus={e => e.target.style.borderColor = '#1D4ED8'}
                    onBlur={e => e.target.style.borderColor = '#E5E7EB'}
                    style={{ ...INPUT, letterSpacing: '0.4em', fontSize: 22, textAlign: 'center', fontWeight: 700 }}
                    autoFocus
                  />
                </div>

                <PrimaryBtn onClick={handleVerify} disabled={loading || code.length !== 6}>
                  {loading ? 'Verifying…' : 'Verify & Sign in'}
                </PrimaryBtn>

                <button onClick={() => { setStep('password'); setCode(''); setError(''); }}
                  style={{ width: '100%', marginTop: 10, padding: '11px', borderRadius: 8, background: 'white', color: '#6B7280', border: '1.5px solid #E5E7EB', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                  ← Back to sign in
                </button>
              </>
            )}

            <p style={{ textAlign: 'center', marginTop: 32, fontSize: 11, color: '#D1D5DB' }}>
              RegShield · CSSF · DORA · GDPR
            </p>
          </div>
        </div>

        {/* ── Right panel — hidden on mobile ── */}
        {!isMobile && <RightPanel />}
      </div>
    </>
  );
};

export default Login;